import { AssetVizMode } from '@asset-viz/defines'
import { Trans } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { ForecastAssetViz } from 'features/assetVizAdapter'
import {
  DEFAULT_SCENE_CONFIG,
  useSlashCommandAssetVizData,
} from 'features/richText/hooks/useSlashCommandAssetVizData'
import { useRolodexEntries } from 'features/rolodex'
import React, { useCallback, useMemo, useState } from 'react'
import {
  GeckoComponentDataFrame,
  getSortedAssetModels,
  RolodexEntryType,
} from 'types'

import { SlashCommandCardProps } from './DefaultSlashCommandCard'
import {
  AssetVizContainer,
  ClickableSlashCommandCardName,
  SlashCommandCard,
  SlashCommandCardBody,
  SlashCommandCardHeader,
  SlashCommandCardIcon,
  SlashCommandCardInfo,
  SlashCommandCardMeta,
  SlashCommandCardType,
  SlashCommandChip,
  SlashCommandChipIcon,
} from './sharedStyles'
import { UnitConfigurationModal } from './UnitConfigurationModal'

const SEARCH_PARAM_INSPECTION_ID = 'inspectionId'
const SEARCH_PARAM_VISUAL_INSPECTION_ID = 'visualInspectionId'

export const InspectionSlashCommandCard: React.FC<SlashCommandCardProps> = ({
  entryType,
  entryID,
  entryName,
  isEditMode,
  onClick,
  metadata,
  onMetadataChange,
}) => {
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()
  const { BASE_PATH } = useRuntimeVariables()

  const handleCloseModal = () => setShowModal(false)

  const { inspections } = useRolodexEntries({
    ids: [entryID],
    type: RolodexEntryType.INSPECTION,
  })

  const inspection = useMemo(() => inspections.at(0), [inspections])
  const componentId = inspection?.tags.component
  const unitId = inspection?.tags.unit

  const unitRolodexOptions = useMemo(
    () =>
      unitId
        ? {
            type: [RolodexEntryType.ASSET_MODEL],
            tags: { unit: unitId },
          }
        : undefined,
    [unitId]
  )

  const { assetModels: assetModelsFromUnit } =
    useRolodexEntries(unitRolodexOptions)

  const componentRolodexOptions = useMemo(
    () =>
      componentId
        ? {
            type: [
              RolodexEntryType.COMPONENT,
              RolodexEntryType.COMPONENT_DATA_FRAME,
              RolodexEntryType.COMPONENT_DATASET,
              RolodexEntryType.COMPONENT_DEFAULTS,
            ],
            tags: { component: componentId },
          }
        : undefined,
    [componentId]
  )

  const {
    components,
    componentDataFrames,
    componentDefaults,
    componentDatasets,
  } = useRolodexEntries(componentRolodexOptions)

  const assetModels = assetModelsFromUnit

  const assetModel = useMemo(
    () => getSortedAssetModels([...assetModels])[0],
    [assetModels]
  )

  const component = useMemo(() => components.at(0), [components])

  const {
    latestComponentDataFrames,
    displayedComponentDataFrames,
    getColorThresholdForComponent,
    getFilteredValueRange,
  } = useSlashCommandAssetVizData({
    componentDataFrames,
    componentDatasets,
    componentDefaults,
    metadata,
    filterCDFs: useCallback(
      (cdf: GeckoComponentDataFrame) => cdf.tags.inspection === entryID,
      [entryID]
    ),
  })

  const handleClick = useCallback(
    (event?: React.MouseEvent) => {
      if (isEditMode) {
        setShowModal(true)
        onClick?.()
      } else if (inspection) {
        const isVisualInspection =
          inspection.type === RolodexEntryType.VISUAL_INSPECTION

        const componentId = inspection.tags.component

        if (!componentId) {
          console.warn('No component ID found for inspection:', entryID)
          return
        }

        const searchParam = isVisualInspection
          ? SEARCH_PARAM_VISUAL_INSPECTION_ID
          : SEARCH_PARAM_INSPECTION_ID

        if (event?.metaKey || event?.ctrlKey) {
          const baseUrl = BASE_PATH ? `/${BASE_PATH}` : ''
          const url = `${baseUrl}/details/${componentId}?${searchParam}=${entryID}`
          window.open(url, '_blank')
          return
        }

        navigate({
          to: '/details/$entryID',
          params: {
            entryID: componentId,
          },
          search: {
            [searchParam]: entryID,
          },
        })
      }
    },
    [inspection, entryID, isEditMode, onClick, navigate, BASE_PATH]
  )

  if (isEditMode) {
    return (
      <>
        <SlashCommandChip
          onClick={handleClick}
          title={`${entryType}: ${entryName}`}>
          <SlashCommandChipIcon>I</SlashCommandChipIcon>
          {entryName}
        </SlashCommandChip>
        {showModal && (
          <UnitConfigurationModal
            entryName={entryName}
            assetModel={assetModel}
            components={component ? [component] : []}
            componentDataFrames={latestComponentDataFrames}
            allComponentDataFrames={[...componentDataFrames]}
            componentDefaults={[...componentDefaults]}
            metadata={metadata}
            onMetadataChange={onMetadataChange}
            onClose={handleCloseModal}
            isSingleComponentView={true}
          />
        )}
      </>
    )
  }

  const inspectionType = inspection?.data.inspection_type

  return (
    <SlashCommandCard $isEditMode={false} $disableHovering>
      <SlashCommandCardHeader>
        <SlashCommandCardIcon>I</SlashCommandCardIcon>
        <SlashCommandCardInfo>
          <ClickableSlashCommandCardName onClick={handleClick}>
            {entryName}
          </ClickableSlashCommandCardName>
          <SlashCommandCardType>
            <Trans>Inspection</Trans>
          </SlashCommandCardType>
        </SlashCommandCardInfo>
      </SlashCommandCardHeader>
      <SlashCommandCardBody $isEditMode={false}>
        <SlashCommandCardMeta>
          <span>
            <Trans>ID: {entryID}</Trans>
          </span>
          {inspectionType && (
            <span>
              <Trans>Type: {inspectionType}</Trans>
            </span>
          )}
        </SlashCommandCardMeta>

        {component && displayedComponentDataFrames.length > 0 && (
          <AssetVizContainer>
            <ForecastAssetViz
              assetModel={assetModel}
              components={[component]}
              componentDataFrames={displayedComponentDataFrames}
              showMultiComponentView={false}
              markupLayers={[]}
              getColorThresholdForComponent={getColorThresholdForComponent}
              cdfToFilteredValueRange={getFilteredValueRange}
              sceneConfig={DEFAULT_SCENE_CONFIG}
              mode={AssetVizMode.SelectBin}
            />
          </AssetVizContainer>
        )}
      </SlashCommandCardBody>
    </SlashCommandCard>
  )
}
